import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { HelpCategory } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { slugify } from '@/features/products';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  emptyHelpCategoryForm,
  fromHelpCategory,
  toHelpCategoryCreate,
  toHelpCategoryUpdate,
} from '../api/mappers';
import { useCreateHelpCategory, useUpdateHelpCategory } from '../hooks/use-help-category-mutations';
import { HANDLE, MAX_POSITION } from '../schemas/help-article-form-schema';
import {
  helpCategoryFormSchema,
  RESERVED_CATEGORY_HANDLES,
  type HelpCategoryForm,
} from '../schemas/help-category-form-schema';

import { VisibilityRadioGroup } from './visibility-radio-group';

const KNOWN_FIELDS: ReadonlySet<string> = new Set([
  'name',
  'handle',
  'description',
  'position',
  'visibility',
]);

export interface HelpCategoryDialogProps {
  storeId: string;
  open: boolean;
  /** Null adds a new category. */
  category: HelpCategory | null;
  /** Prefilled position of a new category: after the last one. */
  nextPosition: number;
  onOpenChange: (open: boolean) => void;
}

function CategoryFormBody({
  storeId,
  category,
  nextPosition,
  onClose,
}: {
  storeId: string;
  category: HelpCategory | null;
  nextPosition: number;
  onClose: () => void;
}) {
  const { t } = useTranslation(['helpCenter', 'common']);
  const message = useErrorMessage();
  const create = useCreateHelpCategory(storeId);
  const update = useUpdateHelpCategory(storeId);
  const pending = create.isPending || update.isPending;
  const [initial] = useState(() =>
    category ? fromHelpCategory(category) : emptyHelpCategoryForm(nextPosition),
  );
  const form = useForm<HelpCategoryForm>({
    resolver: useI18nResolver(helpCategoryFormSchema),
    defaultValues: initial,
  });
  const [handleTouched, setHandleTouched] = useState(category !== null);
  const handle = form.watch('handle').trim();
  const renamed =
    category !== null &&
    handle !== category.handle &&
    HANDLE.test(handle) &&
    !RESERVED_CATEGORY_HANDLES.has(handle);
  const rootError = form.formState.errors.root?.server?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      if (category) {
        const body = toHelpCategoryUpdate(values, initial);
        if (Object.keys(body).length > 0) await update.mutateAsync({ id: category.id, body });
      } else {
        await create.mutateAsync(toHelpCategoryCreate(values));
      }
      onClose();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const limit =
        isApiError(error) && error.code === 'LIMIT_EXCEEDED'
          ? (error.meta as { limit?: number } | undefined)?.limit
          : undefined;
      form.setError('root.server', {
        type: 'server',
        message:
          limit !== undefined ? t('helpCenter:categories.limitReached', { limit }) : message(error),
      });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {rootError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{rootError}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('helpCenter:fields.name')}</FormLabel>
              <FormControl>
                <Input
                  autoFocus
                  autoComplete="off"
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!handleTouched) {
                      form.setValue('handle', slugify(e.target.value), { shouldDirty: true });
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="handle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('helpCenter:fields.handle')}</FormLabel>
              <FormControl>
                <Input
                  autoComplete="off"
                  className="font-mono text-[13px]"
                  {...field}
                  onChange={(e) => {
                    setHandleTouched(true);
                    field.onChange(e);
                  }}
                />
              </FormControl>
              <FormDescription>
                {t('helpCenter:categories.handleHint', { handle: handle || '…' })}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        {renamed ? (
          <Alert role="status">
            <AlertDescription>
              {t('helpCenter:rename.category', {
                from: `/help/${category.handle}`,
                to: `/help/${handle}`,
              })}
            </AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('helpCenter:fields.description')}</FormLabel>
              <FormControl>
                <Textarea rows={3} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('helpCenter:fields.position')}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={MAX_POSITION}
                  className="w-32"
                  {...field}
                />
              </FormControl>
              <FormDescription>{t('helpCenter:fields.positionHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="visibility"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('helpCenter:fields.visibility')}</FormLabel>
              <FormControl>
                <VisibilityRadioGroup
                  idPrefix="help-category-visibility"
                  value={field.value}
                  onChange={field.onChange}
                  hintKey="categoryVisibilityHint"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending
              ? t('common:actions.saving')
              : category
                ? t('common:actions.save')
                : t('helpCenter:categories.create')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Adds or edits a category. The form remounts per open so every edit starts from the row. */
export function HelpCategoryDialog({
  storeId,
  open,
  category,
  nextPosition,
  onOpenChange,
}: HelpCategoryDialogProps) {
  const { t } = useTranslation('helpCenter');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {category ? t('categories.editTitle') : t('categories.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('categories.dialogDescription')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <CategoryFormBody
            key={category?.id ?? 'new'}
            storeId={storeId}
            category={category}
            nextPosition={nextPosition}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
